/**
 * lib/postLoginRoute.js — Fev/2026.
 *
 * Decide para onde o usuário deve ir logo após autenticar (login normal OU
 * primeiro acesso via link mágico de definição de senha). Usado por
 * Login.jsx e SetupSenha.jsx — lógica única, para as duas telas nunca
 * divergirem de novo.
 *
 * Regras:
 *   1. Primeiro acesso de verdade (empresa sem nenhum dado em Gestão — ou,
 *      quando só tem Projetos, sem nenhum projeto ainda) → SEMPRE
 *      `/app/welcome` (boas-vindas + orientação de início pelo wizard).
 *   2. Fora do primeiro acesso, empresa com os 2 módulos (Gestão +
 *      Projetos): Painel V360 é o default — SALVO se o usuário logado não
 *      tiver permissão de acesso ao módulo Gestão (`acesso_gestao`), caso
 *      em que cai no Dashboard do módulo Projetos.
 *   3. Empresa só com Projetos: Dashboard (módulo único disponível). A
 *      permissão `acesso_projetos` é checada, mas hoje não existe outra
 *      tela para onde mandar quem não a tiver — ver nota no código.
 */
import { api } from "@/services/api";

export async function resolvePostLoginRoute(session) {
  if (!session) return "/app/dashboard";
  if (session.is_super_adm) return "/admin";

  // EmpresaPerfil é criado sempre na criação do cliente (admin_clientes.py),
  // então `empresa_perfil_id` faltando é defensivo, não esperado.
  const hasGestao = session.has_v360 === true && !!session.empresa_perfil_id;
  const hasProjetos = session.has_sprints360 === true;
  const podeGestao = session.acesso_gestao !== false;
  const podeProjetos = session.acesso_projetos !== false;
  const emp = session.empresa_perfil_id ? encodeURIComponent(session.empresa_perfil_id) : "";

  if (hasGestao) {
    let status;
    try {
      const { v360EmpresaAPI } = await import("@/services/api");
      status = await v360EmpresaAPI.getStatusInicial(session.empresa_perfil_id);
    } catch (e) {
      console.warn("[postLoginRoute] status-inicial falhou:", e);
      return `/app/v360/wizard?empresa=${emp}`; // fallback histórico (Login.jsx pré-Fev/2026)
    }

    // Regra 1 — primeiro acesso de verdade.
    if (status.route_target === "welcome") {
      return `/app/welcome?empresa=${emp}`;
    }

    // Regra 2 — empresa com os 2 módulos: Painel V360 é o default, salvo
    // sem permissão de Gestão (cai no Dashboard de Projetos).
    if (hasProjetos && !podeGestao) {
      return "/app/dashboard";
    }
    if (status.route_target === "wizard") {
      return `/app/v360/wizard?stage=E${status.next_stage}&empresa=${emp}`;
    }
    return `/app/v360/empresa-dashboard?empresa=${emp}`;
  }

  if (hasProjetos) {
    // Regra 1 (lado Projetos) — primeiro acesso = nenhum projeto criado
    // ainda nesta empresa. Endpoint dedicado e SEM o filtro de
    // autorização por projeto (N3): um usuário restrito com 0 projetos
    // visíveis não pode ser tratado como "empresa vazia" se ela já tiver
    // projetos (só não visíveis para ele). Falha de rede não força welcome
    // (conservador).
    try {
      const resp = await api.get("/sprints360/projetos/existe-algum");
      if (!resp?.existe) return "/app/welcome";
    } catch (e) {
      console.warn("[postLoginRoute] checagem de projetos falhou:", e);
    }
    // Regra 3 — autorização de acesso a Projetos. Hoje sempre true na
    // prática (não há tela de administração que desligue isso ainda) e não
    // existe outro módulo/tela de destino para quem não tiver — mantido
    // explícito para quando essa administração existir.
    if (!podeProjetos) return "/app/dashboard";
    return "/app/dashboard";
  }

  return "/app/dashboard";
}
